import { Directive, ElementRef, Renderer2, OnInit, OnDestroy } from '@angular/core';

@Directive({
  selector: '[toggleMask]',
  standalone: true
})
export class ToggleMaskDirective implements OnInit, OnDestroy {
  private shown = false;
  private buttonEl?: HTMLElement;
  private wrapperEl?: HTMLElement;
  private removeClick?: () => void;
  private removeEyeDown?: () => void;
  private removeBlurListener?: () => void;
  private removeResetListener?: () => void;
  /** Blur fires before eye click toggles in some browsers; relatedTarget may be null. */
  private suppressBlurRevealReset = false;

  constructor(private host: ElementRef<HTMLInputElement>, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.createToggle();
    this.syncState();
    this.renderer.setAttribute(this.host.nativeElement, 'data-show-ssn', 'false');

    this.removeBlurListener = this.renderer.listen(
      this.host.nativeElement,
      'blur',
      (event: FocusEvent) => {
        if (!this.shown) return;
        if (this.suppressBlurRevealReset) return;
        const next = event.relatedTarget as Node | null;
        if (next && this.wrapperEl?.contains(next)) {
          return;
        }
        this.shown = false;
        this.syncState();
        this.renderer.setAttribute(this.host.nativeElement, 'data-show-ssn', 'false');
      }
    );

    this.removeResetListener = this.renderer.listen(this.host.nativeElement, 'resetMask', () => {
      if (this.shown) {
        this.shown = false;
        this.syncState();
        this.renderer.setAttribute(this.host.nativeElement, 'data-show-ssn', 'false');
      }
    });
  }

  private createToggle(): void {
    const wrapper = this.renderer.createElement('span');
    this.wrapperEl = wrapper;
    this.renderer.addClass(wrapper, 'ssn-eye');

    const btn = this.renderer.createElement('button');
    this.buttonEl = btn;
    this.renderer.setAttribute(btn, 'type', 'button');
    this.renderer.addClass(btn, 'btn');
    this.renderer.addClass(btn, 'p-0');
    this.renderer.addClass(btn, 'pi');
    this.renderer.addClass(btn, 'eye');
    // Add aria-label for screen readers
    this.renderer.setAttribute(btn, 'aria-label', 'Hide/Visible');
    this.renderer.setAttribute(btn, 'title', 'Show');

    this.renderer.appendChild(wrapper, btn);
    const parent = this.host.nativeElement.parentNode as HTMLElement;
    if (parent) this.renderer.appendChild(parent, wrapper);

    this.removeEyeDown = this.renderer.listen(btn, 'mousedown', () => {
      this.suppressBlurRevealReset = true;
      setTimeout(() => {
        this.suppressBlurRevealReset = false;
      }, 0);
    });

    this.removeClick = this.renderer.listen(btn, 'click', () => this.toggle());
  }

  private toggle(): void {
    this.shown = !this.shown;
    this.syncState();
    this.renderer.setAttribute(this.host.nativeElement, 'data-show-ssn', String(this.shown));
    if (this.shown && this.host.nativeElement !== document.activeElement) {
      this.host.nativeElement.focus();
    }
  }

  private syncState(): void {
    if (!this.buttonEl) return;
    if (this.shown) {
      this.renderer.removeClass(this.buttonEl, 'pi-eye-slash');
      this.renderer.addClass(this.buttonEl, 'pi-eye');
      this.renderer.setAttribute(this.buttonEl, 'aria-label', 'Hide/Visible');
      this.renderer.setAttribute(this.buttonEl, 'title', 'Hide');
    } else {
      this.renderer.removeClass(this.buttonEl, 'pi-eye');
      this.renderer.addClass(this.buttonEl, 'pi-eye-slash');
      this.renderer.setAttribute(this.buttonEl, 'aria-label', 'Hide/Visible');
      this.renderer.setAttribute(this.buttonEl, 'title', 'Show');
    }
  }

  ngOnDestroy(): void {
    if (this.removeClick) this.removeClick();
    if (this.removeEyeDown) this.removeEyeDown();
    if (this.removeBlurListener) this.removeBlurListener();
    if (this.removeResetListener) this.removeResetListener();
    if (this.wrapperEl?.parentNode) {
      this.renderer.removeChild(this.wrapperEl.parentNode, this.wrapperEl);
    }
  }
}